import React, { Fragment, useState } from 'react';
import _ from 'lodash';
import { useObserver } from 'mobx-react';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import styled from 'styled-components';
import { space } from 'styled-system';
import { Link } from 'src/components/core';

import {
  filterLegacyEffects,
  isWeightedProduct,
  getFormattedPotency,
  getQuantityRemaining,
  getSpecialBadgeType,
  getSalesType,
  getTaxInclusionNote,
  getCategoryForProduct,
  strainEffects,
} from 'shared/helpers/products';
import { tracker } from 'src/utils/analytics';
import { groupOptionsWithPrices } from 'src/utils/helpers/product';
import { getDescriptionHtml, isDescriptionHtmlBlank } from 'shared/utils/product-description-utils';
import { thcDescription, cbdDescription, tacDescription, POTENCY_TYPES } from 'shared/constants/products';
import { linkColors } from 'shared/constants/menu-colors';

import { HorizontalDivider } from 'components/core/divider';
import { StaffPickChip, ComingSoonChip } from 'components/chips';
import SanitizedHtml from 'shared/components/sanitized-html';

import useDispensary from 'src/dispensary/hooks/use-dispensary';
import useUI from 'hooks/use-ui';
import useCart from 'hooks/use-cart';
import useTranslation from 'hooks/use-translation';
import useProductPrices from 'hooks/use-product-prices';
import useProductQueryParams from 'hooks/use-product-query-params';
import { useViewedProductAnalytics } from 'hooks/use-ecommerce-analytics';
import useDispensaryFlag from 'shared/hooks/use-dispensary-flag';
import usePaths from 'src/hooks/use-paths';

import { isExcludedCannabinoid } from 'shared/constants/cannabinoids';
import useProductOffers from 'src/hooks/use-product-offers';
import ProductSpecialsCarousel from 'components/product-specials-carousel';
import { useRouter } from 'next/router';
import { productIsPartOfActiveSpecial } from 'shared/helpers/specials';
import { useViewedProductMonitor } from 'utils/analytics/monitors/use-viewed-product-monitor';
import { useAddToCartModal } from 'components/modals/add-to-cart';
import AdditionalOptionsSelect from './additional-options-select';
import QuantitySelect from './quantity-select';
import OptionsList from './options-list';
import { AddToCartButton, BuyingOptionsButton } from './buttons';
import { AvailableFrom, Brand, Description, Name } from './typography';
import { Content, Images, Details, AddToCartContainer, CarouselContainer, TaxText } from './layout';
import { Price, PriceFrom } from './price';
import { InfoChip } from '../info-chip';
import ProductDetails from './product-details';

// eslint-disable-next-line i18next/no-literal-string
const TagTooltipWidth = '348px';

export default function Product(props) {
  const { queryParams } = useProductQueryParams();
  const { product } = props;
  const { isKiosk } = useUI();
  const { open: openAddToCartModal } = useAddToCartModal();
  const { dispensary, loading: isDispensaryLoading } = useDispensary();
  const { t } = useTranslation();
  const { standardPrice, specialPrice, weightedSpecialPrices } = useProductPrices(product, queryParams.weight);
  const UI = useUI();
  const cart = useCart();
  const router = useRouter();
  const isMedical = useObserver(() => cart.isMedical);
  const { href, route } = usePaths();
  const taxInclusionNote =
    !isDispensaryLoading && dispensary && getTaxInclusionNote({ product, dispensary, medical: isMedical });
  const menuType = useObserver(() => cart.menuType);
  const linkColorData = _.find(linkColors, ({ key }) => key === dispensary?.colorSettings?.linkColor);
  const dispensaryCarouselDotColor = useObserver(() => linkColorData?.background ?? linkColors?.['blue-4']?.background);

  useViewedProductAnalytics({ product, cart: _.cloneDeep(cart) });
  useViewedProductMonitor(product);

  const parsedOptions = groupOptionsWithPrices({ product, menuType, isKiosk });
  const [selectedOption, setSelectedOption] = useState(parsedOptions[0]);
  const [selectedAdditionalOption, setAdditionalSelectedOption] = useState(product?.AdditionalOptions?.[0]);
  const [quantity, setQuantity] = useState(1);

  // eslint-disable-next-line no-restricted-globals
  const discountDisplay = (percentage) =>
    !Number.isFinite(percentage) || percentage <= 0 ? null : `${percentage}% off`;

  const isFeatured = product.featured?.current;
  const comingSoonProductsFlagEnabled = useDispensaryFlag(`rollout.coming-soon-products`, product.DispensaryID);
  const isComingSoon = comingSoonProductsFlagEnabled && product.comingSoon;
  const lowestPrice = !_.isNil(specialPrice) ? specialPrice : standardPrice;
  const quantityRemaining = parseQuantityRemaining(selectedOption.value);
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down(`sm`));
  const { specials } = useProductOffers(product);

  const filteredEffects = filterLegacyEffects(product.effects);
  const hasEffects = !_.isEmpty(filteredEffects);
  const hasTerpenes = !_.isEmpty(product.terpenes) && !!product.terpenes.find((terp) => terp.value > 0);
  const showSpecialOffersCarousel = productIsPartOfActiveSpecial(product, menuType);

  product.cannabinoids = product.cannabinoidsV2;
  const hasCannabinoids =
    !_.isEmpty(product.cannabinoids) &&
    !_.isEmpty(_.filter(product.cannabinoids, ({ cannabinoid }) => !isExcludedCannabinoid(cannabinoid?.name)));

  const hideEffects = _.get(dispensary, `storeSettings.hideEffects`, false);
  const hideTerpenes = _.get(dispensary, `storeSettings.hideTerpenes`, false);

  const hasDescription = !isDescriptionHtmlBlank(getDescriptionHtml(product));
  const hasBrandDescription = !_.isEmpty(_.trim(product.brand?.description));
  const brandSlug = _.kebabCase(product.brand?.name) || product.brand?.id;
  const potency = getFormattedPotency(product);

  const tags = [];

  if (product.strainType && product.strainType !== 'N/A') {
    tags.push({
      highlight: true,
      titleCase: true,
      id: `strainType`,
      text: product.strainType,
    });
  }

  if (potency.TAC && potency.TAC !== '--') {
    tags.push({
      id: `tac-content`,
      text: (
        <Fragment>
          <b>{POTENCY_TYPES.TAC}:</b> {potency.TAC}
        </Fragment>
      ),
      moreInfo: {
        infoTitle: t('marketplace.what-is-tac-header', 'What is TAC?'),
        infoBody: (
          <span>
            <b>{t('marketplace.tac-description-head', tacDescription.head)}&nbsp;</b>
            {t('marketplace.tac-description-body', tacDescription.body)}
          </span>
        ),
        tooltipDimensions: {
          width: TagTooltipWidth,
        },
      },
    });
  }

  if (potency.THC && potency.THC !== '--') {
    tags.push({
      id: `thc-content`,
      text: (
        <Fragment>
          <b>{POTENCY_TYPES.THC}:</b> {potency.THC}
        </Fragment>
      ),
      moreInfo: {
        infoTitle: t('marketplace.what-is-thc-header', 'What is THC?'),
        infoBody: (
          <span>
            <b>{t('marketplace.thc-description-head', thcDescription.head)}&nbsp;</b>
            {t('marketplace.thc-description-body', thcDescription.body)}
          </span>
        ),
        tooltipDimensions: {
          width: TagTooltipWidth,
        },
      },
    });
  }

  if (potency.CBD && potency.CBD !== '--') {
    tags.push({
      id: `cbd-content`,
      text: (
        <Fragment>
          <b>{POTENCY_TYPES.CBD}:</b> {potency.CBD}
        </Fragment>
      ),
      moreInfo: {
        infoTitle: t('marketplace.what-is-cbd-header', 'What is CBD?'),
        infoBody: (
          <span>
            <b>{t('marketplace.cbd-description-head', cbdDescription.head)}&nbsp;</b>
            {t('marketplace.cbd-description-body', cbdDescription.body)}
          </span>
        ),
        tooltipDimensions: {
          width: TagTooltipWidth,
        },
      },
    });
  }

  const hasTags = tags.length > 0;

  function handleToggleBuyingOptionsModal() {
    UI.productBuyingOptionsModal = product;
  }

  function parseQuantityRemaining(weight) {
    return _.clamp(getQuantityRemaining(product, weight, { isKiosk }), 1, 10);
  }

  function handleOptionSelect(option) {
    const newQuantityRemaining = parseQuantityRemaining(option?.value);

    if (quantity > newQuantityRemaining) {
      setQuantity(1);
    }

    setSelectedOption(option);
  }

  function handleQuantityChange({ target }) {
    setQuantity(target.value);
  }

  const handleAddToCart = async () => {
    tracker.setContext({ activeProductPosition: { fromCarousel: false } });
    openAddToCartModal({
      quantity,
      additionalOption: selectedAdditionalOption,
      option: selectedOption.value,
      product,
      dispensary,
    });
  };

  const salesType = getSalesType(product);
  const badgeType = getSpecialBadgeType(salesType, product?.specialData?.saleSpecials, product?.Options);
  const showWeightSpecials = badgeType !== `none`;

  async function handleCarouselClick(offerId) {
    await router.push(`/${UI.dispensaryRootUrl}/${dispensary?.cName}/specials/offer/${offerId}`);
  }

  async function navigateToStrainTypePage(tag) {
    // eslint-disable-next-line lodash/prefer-lodash-method
    const isInfoTextLink = tag.id === `strainType` && Object.keys(strainEffects).some((st) => st === tag.text);
    const dispensaryCName = dispensary?.cName;
    const category = getCategoryForProduct(product).key;

    if (isInfoTextLink && dispensaryCName && category) {
      // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
      await router.push(`/${UI.dispensaryRootUrl}/${dispensaryCName}/products/${category}?strainTypes=${tag.text}`);
    }
  }

  return (
    <Content isMobile={isMobile}>
      <Images product={product} isMobile={isMobile} />
      <Details isMobile={isMobile}>
        {product.brand && product.brand?.id && (
          <Brand isMobile={isMobile}>
            <Link route={`${route}/brands/${brandSlug}`} href={`${href}/brands/${brandSlug}`}>
              {product.brand.name}
            </Link>
          </Brand>
        )}
        <Name tag='h1' size='xlarge'>
          {product.name}
        </Name>
        {(isFeatured || isComingSoon) && (
          <ChipContainer mb='16px'>
            {isFeatured && <StaffPickChip />}
            {isComingSoon && <ComingSoonChip />}
          </ChipContainer>
        )}
        {dispensary && (
          <React.Fragment>
            {isWeightedProduct(product) ? (
              <OptionsList
                discountDisplay={discountDisplay}
                onOptionSelect={handleOptionSelect}
                options={parsedOptions}
                selectedOption={selectedOption}
                showWeightSpecials={showWeightSpecials}
                weightedSpecialPrices={weightedSpecialPrices}
                disabled={isComingSoon}
              />
            ) : (
              <Price
                price={lowestPrice}
                isSpecial={!_.isNil(specialPrice)}
                standardPrice={standardPrice}
                isMobile={isMobile}
              />
            )}

            {!UI.viewOnlyMode && !isComingSoon && (
              <AddToCartContainer>
                {!_.isEmpty(product?.AdditionalOptions) && (
                  <AdditionalOptionsSelect
                    options={product.AdditionalOptions}
                    value={selectedAdditionalOption}
                    onChange={setAdditionalSelectedOption}
                  />
                )}
                <QuantitySelect
                  onChange={handleQuantityChange}
                  value={quantity}
                  quantityRemaining={quantityRemaining}
                />
                <ButtonWrapper isNewLine={!_.isEmpty(product?.AdditionalOptions)}>
                  <AddToCartButton isMobile={isMobile} onClick={handleAddToCart} id='add-to-cart' />
                </ButtonWrapper>
              </AddToCartContainer>
            )}
            {taxInclusionNote && <TaxText>{taxInclusionNote}</TaxText>}
          </React.Fragment>
        )}
        {!dispensary && (
          <React.Fragment>
            <PriceFrom price={lowestPrice} />
            <HorizontalDivider mb={31} />
            <AvailableFrom numStores={7} />
            <BuyingOptionsButton onClick={handleToggleBuyingOptionsModal} />
          </React.Fragment>
        )}
        {showSpecialOffersCarousel && (
          <React.Fragment>
            <HorizontalDivider mb={isMobile ? 31 : 38} />
            <CarouselContainer>
              <ProductSpecialsCarousel
                specials={specials}
                dotColor={dispensaryCarouselDotColor}
                onClick={handleCarouselClick}
              />
            </CarouselContainer>
          </React.Fragment>
        )}
        {/* Tags & Description */}
        {(hasTags || hasDescription) && (
          <React.Fragment>
            <HorizontalDivider mb={isMobile ? 31 : 38} />
            {hasTags && (
              <Tags isMobile={isMobile}>
                {_.map(tags, (tag) => (
                  <InfoChip key={tag.id} {...tag} onClick={() => navigateToStrainTypePage(tag)} />
                ))}
              </Tags>
            )}
            {hasDescription && (
              <Description>
                <SanitizedHtml html={getDescriptionHtml(product)} omitBrs />
              </Description>
            )}
          </React.Fragment>
        )}
        <ProductDetails
          product={product}
          hasEffects={hasEffects}
          hideEffects={hideEffects}
          hasTerpenes={hasTerpenes}
          hideTerpenes={hideTerpenes}
          hasCannabinoids={hasCannabinoids}
          hasBrandDescription={hasBrandDescription}
          isMobile={isMobile}
        />
      </Details>
    </Content>
  );
}

const ChipContainer = styled.div`
  ${space}

  .MuiChip-root {
    font-size: 14px;
    padding: 3px;
    margin-right: 8px;
  }
`;

const Tags = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  margin-bottom: ${({ isMobile }) => (isMobile ? `23px` : `32px`)};
`;

const ButtonWrapper = styled.div`
  @media only screen and (max-width: 450px) {
    margin-top: ${({ isNewLine }) => (isNewLine ? `15px` : `0px`)};
  }
`;
